/**
 * Add / edit form for a federated identity provider (FR-AUTH-01/02/06).
 *
 * Validation runs client-side first (so a typo never costs a round trip) and
 * server-side field errors from the auth service are merged into the same
 * inline slots. The persona checkbox grid is the FR-AUTH-02 policy: at least
 * one persona must stay ticked, and `platform_admin` is not offered at all.
 */
import { useMemo, useState, type FormEvent } from 'react';

import { TextField } from '../../components/text-field';
import { ApiError } from '../../lib/api-error';
import { PERSONA_LABELS, type Persona } from '../../lib/personas';

import {
  CONFIGURABLE_PERSONAS,
  PROTOCOL_LABELS,
  type FederationProtocol,
  type IdentityProviderView,
} from './use-identity-providers';

const SLUG_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const CERT_HEADER = '-----BEGIN';

export interface ProviderFormValues {
  protocol: FederationProtocol;
  slug: string;
  display_name: string;
  saml_idp_entity_id: string;
  saml_sso_url: string;
  saml_issuer_cert_pem: string;
  scim_bearer_token_ref: string;
  allowed_personas: Persona[];
  default_persona: Persona | '';
  is_enabled: boolean;
}

export interface IdentityProviderFormProps {
  /** Present when editing; absent when adding. */
  provider?: IdentityProviderView;
  onCancel: () => void;
  onSubmit: (values: ProviderFormValues) => Promise<void>;
}

type FieldErrors = Partial<Record<keyof ProviderFormValues, string>>;

function initialValues(provider?: IdentityProviderView): ProviderFormValues {
  return {
    protocol: provider?.protocol ?? 'saml2',
    slug: provider?.slug ?? '',
    display_name: provider?.display_name ?? '',
    saml_idp_entity_id: provider?.saml_idp_entity_id ?? '',
    saml_sso_url: provider?.saml_sso_url ?? '',
    saml_issuer_cert_pem: '',
    scim_bearer_token_ref: '',
    allowed_personas: provider?.allowed_personas ?? ['team_admin', 'coach'],
    default_persona: provider?.default_persona ?? '',
    is_enabled: provider?.is_enabled ?? true,
  };
}

/** Every rule the auth service enforces, checked here first. */
export function validateProviderForm(values: ProviderFormValues, isEditing: boolean): FieldErrors {
  const errors: FieldErrors = {};
  if (values.display_name.trim() === '') errors.display_name = 'Give the provider a name.';
  if (!isEditing) {
    if (values.slug.trim() === '') errors.slug = 'An identifier is required.';
    else if (!SLUG_PATTERN.test(values.slug.trim())) {
      errors.slug = 'Use lowercase letters, digits and hyphens only.';
    }
  }
  if (values.protocol === 'saml2') {
    if (values.saml_idp_entity_id.trim() === '') {
      errors.saml_idp_entity_id = 'Copy the Entity ID from your IdP metadata.';
    }
    if (values.saml_sso_url.trim() !== '' && !/^https?:\/\//i.test(values.saml_sso_url.trim())) {
      errors.saml_sso_url = 'Enter a full https:// URL.';
    }
    const certificate = values.saml_issuer_cert_pem.trim();
    if (!isEditing && certificate === '') {
      errors.saml_issuer_cert_pem = 'Paste or upload the IdP signing certificate.';
    } else if (certificate !== '' && !certificate.includes(CERT_HEADER)) {
      errors.saml_issuer_cert_pem = 'That does not look like a PEM certificate.';
    }
  }
  if (values.allowed_personas.length === 0) {
    errors.allowed_personas = 'Allow at least one persona to sign in with this provider.';
  }
  if (values.default_persona !== '' && !values.allowed_personas.includes(values.default_persona)) {
    errors.default_persona = 'The default persona must also be allowed.';
  }
  return errors;
}

/** Merge `details.fields` from an RFC 7807 body into the inline slots. */
function serverFieldErrors(caught: unknown): FieldErrors {
  if (!ApiError.isApiError(caught)) return {};
  const fields = caught.details?.fields;
  const errors: FieldErrors = {};
  if (typeof fields === 'object' && fields !== null && !Array.isArray(fields)) {
    for (const [key, message] of Object.entries(fields as Record<string, unknown>)) {
      errors[key as keyof ProviderFormValues] = String(message);
    }
  }
  if (caught.field) errors[caught.field as keyof ProviderFormValues] = caught.message;
  return errors;
}

export function IdentityProviderForm({
  provider,
  onCancel,
  onSubmit,
}: IdentityProviderFormProps): JSX.Element {
  const isEditing = provider !== undefined;
  const [values, setValues] = useState<ProviderFormValues>(() => initialValues(provider));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const personaOptions = useMemo(
    () => CONFIGURABLE_PERSONAS.map((persona) => ({ persona, label: PERSONA_LABELS[persona] })),
    []
  );

  function patch(next: Partial<ProviderFormValues>): void {
    setValues((current) => ({ ...current, ...next }));
  }

  function togglePersona(persona: Persona, isChecked: boolean): void {
    setValues((current) => {
      const allowed = isChecked
        ? [...current.allowed_personas, persona]
        : current.allowed_personas.filter((entry) => entry !== persona);
      return {
        ...current,
        allowed_personas: allowed,
        default_persona: allowed.includes(current.default_persona as Persona)
          ? current.default_persona
          : '',
      };
    });
  }

  async function readCertificateFile(file: File | undefined): Promise<void> {
    if (!file) return;
    patch({ saml_issuer_cert_pem: await file.text() });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const found = validateProviderForm(values, isEditing);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setFormError('Fix the highlighted fields and try again.');
      return;
    }
    setFormError(null);
    setIsSaving(true);
    try {
      await onSubmit(values);
    } catch (caught) {
      const fields = serverFieldErrors(caught);
      setErrors(fields);
      setFormError(
        ApiError.isApiError(caught)
          ? caught.message
          : 'The provider could not be saved. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form
      className="desk-form desk-form--two-col desk-form--stacked federation-form"
      noValidate
      onSubmit={(event) => void handleSubmit(event)}
    >
      {formError === null ? null : (
        <p className="form-error desk-form__full" role="alert">
          {formError}
        </p>
      )}

      {isEditing ? null : (
        <fieldset className="field desk-form__full federation-form__protocols">
          <legend className="field__label">Protocol</legend>
          {(Object.keys(PROTOCOL_LABELS) as FederationProtocol[]).map((protocol) => (
            <label key={protocol} className="checkbox">
              <input
                type="radio"
                name="protocol"
                value={protocol}
                checked={values.protocol === protocol}
                onChange={() => patch({ protocol })}
              />
              <span>
                <strong>{PROTOCOL_LABELS[protocol]}</strong>
                <span className="muted">
                  {protocol === 'saml2'
                    ? ' — staff sign in with your school IdP.'
                    : ' — your directory pushes accounts to Gridiron.'}
                </span>
              </span>
            </label>
          ))}
        </fieldset>
      )}

      <TextField
        label="Display name"
        required
        value={values.display_name}
        error={errors.display_name ?? null}
        hint="Shown on the sign-in page and in the audit log."
        onChange={(event) => patch({ display_name: event.target.value })}
      />

      <TextField
        label="Identifier"
        required={!isEditing}
        disabled={isEditing}
        value={values.slug}
        error={errors.slug ?? null}
        hint="Lowercase, URL-safe. Cannot be changed later."
        placeholder="riverside-okta"
        onChange={(event) => patch({ slug: event.target.value.toLowerCase() })}
      />

      {values.protocol === 'saml2' ? (
        <>
          <TextField
            label="IdP Entity ID"
            required
            value={values.saml_idp_entity_id}
            error={errors.saml_idp_entity_id ?? null}
            hint="From your IdP's metadata document, e.g. https://sso.school.edu/idp."
            onChange={(event) => patch({ saml_idp_entity_id: event.target.value })}
          />
          <TextField
            label="IdP SSO URL"
            value={values.saml_sso_url}
            error={errors.saml_sso_url ?? null}
            hint="Optional. Used for IdP-initiated sign-in links."
            onChange={(event) => patch({ saml_sso_url: event.target.value })}
          />
          <div
            className={`field desk-form__full${errors.saml_issuer_cert_pem ? ' field--invalid' : ''}`}
          >
            <label className="field__label" htmlFor="saml-cert">
              Signing certificate (PEM)
              {isEditing ? null : <span aria-hidden="true"> *</span>}
            </label>
            <textarea
              id="saml-cert"
              className="field__control"
              rows={5}
              spellCheck={false}
              placeholder="-----BEGIN CERTIFICATE-----"
              value={values.saml_issuer_cert_pem}
              onChange={(event) => patch({ saml_issuer_cert_pem: event.target.value })}
            />
            <p className="field__hint">
              {isEditing
                ? 'Leave blank to keep the certificate already on file.'
                : 'Paste the PEM block, or upload the .pem / .crt file your IdP exports.'}
            </p>
            <input
              type="file"
              accept=".pem,.crt,.cer,text/plain"
              aria-label="Upload certificate file"
              onChange={(event) => void readCertificateFile(event.target.files?.[0])}
            />
            {errors.saml_issuer_cert_pem ? (
              <p className="field__error" role="alert">
                {errors.saml_issuer_cert_pem}
              </p>
            ) : null}
          </div>
        </>
      ) : (
        <TextField
          label="Secret reference (optional)"
          value={values.scim_bearer_token_ref}
          error={errors.scim_bearer_token_ref ?? null}
          hint="Kubernetes Secret key where your operations team mirrors the token."
          placeholder="scim-tokens/riverside"
          onChange={(event) => patch({ scim_bearer_token_ref: event.target.value })}
        />
      )}

      <fieldset
        className={`field desk-form__full${errors.allowed_personas ? ' field--invalid' : ''}`}
      >
        <legend className="field__label">
          Allowed personas <span aria-hidden="true">*</span>
        </legend>
        <p className="field__hint">
          Only these personas may sign in through this provider. Platform Admin accounts are managed
          by Gridiron and can never be federated.
        </p>
        <div className="persona-grid">
          {personaOptions.map(({ persona, label }) => (
            <label key={persona} className="checkbox checkbox--compact">
              <input
                type="checkbox"
                checked={values.allowed_personas.includes(persona)}
                onChange={(event) => togglePersona(persona, event.target.checked)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        {errors.allowed_personas ? (
          <p className="field__error" role="alert">
            {errors.allowed_personas}
          </p>
        ) : null}
      </fieldset>

      <div className={`field${errors.default_persona ? ' field--invalid' : ''}`}>
        <label className="field__label" htmlFor="default-persona">
          Default persona
        </label>
        <select
          id="default-persona"
          className="field__control"
          value={values.default_persona}
          onChange={(event) => patch({ default_persona: event.target.value as Persona | '' })}
        >
          <option value="">No default — the assertion must say</option>
          {values.allowed_personas.map((persona) => (
            <option key={persona} value={persona}>
              {PERSONA_LABELS[persona]}
            </option>
          ))}
        </select>
        <p className="field__hint">
          Applied when the assertion or SCIM push does not name a persona.
        </p>
        {errors.default_persona ? (
          <p className="field__error" role="alert">
            {errors.default_persona}
          </p>
        ) : null}
      </div>

      <label className="checkbox checkbox--compact federation-form__active">
        <input
          type="checkbox"
          checked={values.is_enabled}
          onChange={(event) => patch({ is_enabled: event.target.checked })}
        />
        <span>Provider is active</span>
      </label>

      <div className="btn-row btn-row--end dialog-actions desk-form__full">
        <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={isSaving}>
          Cancel
        </button>
        <button type="submit" className="btn btn--primary" disabled={isSaving}>
          {isSaving ? 'Saving…' : isEditing ? 'Save changes' : 'Add provider'}
        </button>
      </div>
    </form>
  );
}
